<template>
  <div class="container">
    <div class="row">
      <section class="flight-booking-details">

        <div class="booking-details-body">
          <div class="booking-details-main">
            <h1 v-if="isConfirmation">{{ $t('yourBookingIsConfirmed') }}</h1>

            <h1 v-else>{{ $t('flightBooking') }}</h1>

            <div v-if="documents > 0 && authenticated" class="booking-event-history">
              <div class="details">
                <p>{{ isTotalTravelEvent(event.b2b_eventType) ? 'TotalTravel Event:' : 'ETG Event:' }} {{event.b2b_eventType}} {{event.b2b_timestamp ? '- ' + event.b2b_timestamp : ''}}</p>
                <p>Event Process Date: {{documentDate}}</p>
              </div>

            </div>


            <p v-if="error">
              {{ $t('orderLookupFailedParagraph') }} <br /> <br />
              <a @click="$router.go(-1)" href="javascript:" :title="$t('pleaseGoBackAndTryAgain')">{{ $t('pleaseGoBackAndTryAgain') }}</a>
            </p>

            <div v-if="order">
              <div class="booking-details-section">   
                <div class="content-panel">
                  <div class="panel-title">
                    <h3>
                      <span class="booking-status-label" v-if="order.orderState === 'ACTIVE'">{{ $t('confirmed') }}</span>
                      <span class="booking-status-label cancelled" v-if="order.orderState === 'CANCELED'">{{ $t('cancelled') }}</span> 
                      {{ $t('bookingReference') }}: {{ order.orderReference }}
                    </h3>
                  </div>

                  <div class="panel-body">
                    <div class="flight-listing flight-listing-summary">
                      <div class="flight-details">
                        <div class="bound" v-for="(bound, boundIndex) in airProduct.customerItem.productItem.current.bounds" :key="'bound' + boundIndex">
                          <div class="title-bar">
                            <p>
                              <span class="type">{{boundIndex == 0 ? $t('outbound') : $t('return')}}</span>
                              {{bound.segments[0].from.iataCode | toCityName}} - {{(bound.segments[bound.segments.length - 1].to.iataCode) | toCityName}}
                            </p>
                            <p>
                              <span class="time">
                                <i class="fal fa-clock" aria-hidden="true"></i>
                                {{bound.totalTravelTime | parseDuration}}
                              </span>
                            </p>
                          </div>

                          <div class="details-block">
                            <div class="segment-info" v-for="(segment, segmentIndex) in bound.segments" :key="'segment' + segmentIndex">
                              <div class="date">
                                <p>{{$moment(segment.from.date).format('ddd, MMM DD')}}</p>
                                <p>{{ $t('bookingReference') }}:<br />{{findAirlineReservationReferences(airProduct, boundIndex, segmentIndex)}}</p>
                              </div>
                              <div class="route">
                                <p class="times">
                                  {{$moment(segment.from.time, 'HH:mm').format('h:mma')}}
                                  <span class="divider"> - </span>
                                  {{$moment(segment.to.time, 'HH:mm').format('h:mma')}} &nbsp; {{(segment.travelTime) | parseDuration}}
                                </p>
                                <p class="airports">
                                  {{segment.from.iataCode | toAirportName}} ({{segment.from.iataCode}}) to {{(segment.to.iataCode) | toAirportName}} ({{segment.to.iataCode}})
                                </p>
                              </div>
                              <div class="airline">
                                <p><img :src="`https://assets.totaltravel.io/img/airlines/${segment.marketingCarrier.iataCode}.png`" :alt="segment.marketingCarrier.iataCode | toAirlineName" width="30px" height="auto" /></p>
                                <p>
                                  {{(segment.marketingCarrier.iataCode) | toAirlineName}} ({{segment.marketingCarrier.iataCode}})<br />
                                  {{ $t('flight') }} #: {{segment.flight}}<br />
                                  {{ $t('class') }}: {{segment.cabinClass}}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="booking-details-section">   
                <div class="content-panel">
                  <div class="panel-title">
                    <h3>{{ $t('passengerDetails') }}</h3>
                  </div>

                  <div class="panel-body">
                    <div class="all-passenger-info">
                        <h5>{{ $t('primaryPassengerContactInformation') }}</h5>
                        <p><strong>{{ $t('emailAddress') }}:</strong> {{order.travelers[0].email}}</p>
                        <p><strong>{{ $t('phoneNumber') }}:</strong> +{{order.travelers[0].phoneNumber.countryCallingCode}} {{order.travelers[0].phoneNumber.numberWithoutCountryCode}}</p>
                    </div>

                    <div class="passenger-list">
                      <div class="passenger" v-for="(traveler, travelerIndex) in order.travelers" :key="'traveler' + travelerIndex">
                        <div class="info">
                          <p><strong>{{ $t('passenger') }} {{travelerIndex + 1}} ({{traveler.travelerReference}}):</strong></p>
                          <p>{{ $t('name') }}: {{traveler.firstName}} {{traveler.lastName}}</p>
                          <p>{{ $t('gender') }}: {{traveler.gender === 'MALE' ? $t('male') : $t('female')}}</p>
                          <p>{{traveler.dateOfBirth ? $t('dateOfBirth') + ` ${traveler.dateOfBirth}` : ''}}</p>
                        </div>
                        <div class="add-ons">
                          <p><strong>{{ $t('addOns') }}</strong></p>
                          <ul>
                            <li v-for="(orderItem, orderItemIndex) in findExtraProductsForTravelerReference(order.primaryOrder.orderItems, traveler.travelerReference)" 
                            :key="'orderItem' + orderItemIndex">
                              {{orderItem}}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="booking-details-section">   
                <div class="content-panel">
                  <div class="panel-title">
                    <h3>{{ $t('summaryOfCharges') }}</h3>
                    <div class="paid-checkbox" v-if="metadata && authenticated">
                      <label for="paidCheckbox">{{ $t('paid') }}</label>
                      <input id="paidCheckbox" type="checkbox" @change="updateOrderServiceFee()" v-model="metadata.paid" />
                    </div>
                  </div>

                  <div class="panel-body">

                    <div class="charges-summary">

                      <div class="summary" v-for="(price, priceIndex) in airProduct.customerItem.productItem.current.travelerPrices" :key="'price' + priceIndex">
                        <p> {{ $t('passenger') }} {{priceIndex + 1}} ({{price.reference}}):</p>
                        <p>{{price.price.total | formatCurrency(order.currency, 2)}}</p>
                      </div>

                      <div class="summary" v-for="(orderItem, orderItemIndex) in order.primaryOrder.orderItems" :key="'orderItem' + orderItemIndex" v-if="orderItem.customerItem.productItem.productType.startsWith('EXTRA.')">
                        <p>{{toAncillaryName(orderItem.customerItem.productItem.productType)}}:</p>
                        <p>{{orderItem.customerItem.price.total | formatCurrency(order.currency, 2)}}</p>
                      </div>

                      <div class="summary">
                        <p>{{ $t('taxes') }}:</p>
                        <p>({{ $t('included') }}) {{calculateTotalTaxes(order.primaryOrder.orderItems) | formatCurrency(order.currency, 2)}}</p>
                      </div>

                      <div class="summary" v-if="metadata && authenticated">
                        <p><strong>{{ $t('serviceFee') }}:</strong></p>
                        <p class="edit">
                          {{order.currency | currencySymbol}} <input min="0" type="number" v-model="metadata.serviceFee" />
                          <button class="btn btn-sec btn-sm" @click="updateOrderServiceFee()">{{$t('update')}}</button>
                        </p>
                      </div>

                      <div class="summary" v-if="metadata && !authenticated">
                        <p>{{ $t('serviceFee') }}:</p>
                        <p>{{metadata.serviceFee | formatCurrency(order.currency, 2)}}</p>
                      </div>


                      <div class="summary total">
                        <p><strong>{{ $t('total') }}:</strong></p>
                        <p><strong>{{ (order.primaryOrder.orderAmount.total + (metadata ? parseFloat(metadata.serviceFee) : 0)) | formatCurrency(order.currency, 2)}}</strong></p>
                      </div>
                    </div>
                  
                  </div>
                </div>
              </div>

              <div class="booking-details-section" v-if="site.messaging.flights.confirmationPage.content && site.messaging.flights.confirmationPage.content.length > 0">
                <div class="content-panel">
                  <div class="panel-title">
                    <h3>{{ $t('importantInformation') }}</h3>
                  </div>

                  <div class="panel-body">
                    <div v-html="site.messaging.flights.confirmationPage.content"></div>
                  </div>
                </div>
              </div>
            </div>

            <div class="loading-details" v-if="!order && !error">
              <i class="fal fa-spin fa-spinner-third"></i>
              <span>{{ $t('loadingBookingDetails') }}</span>
            </div>
 
          </div>

          <aside class="booking-details-aside" v-if="order">
            <div class="aside-panel booking-resend-email">
              <h4>{{ $t('shareByEmail') }}</h4>

              <div class="form-group" v-if="!emailSent && !emailSending && !emailSendFailed">
                <p>{{ $t('shareByEmailParagraph') }}</p>
                <input type="text" name="Email Address" ref="email" data-vv-scope="emailForm" v-model="sendEmailAddress" v-validate="'required|email'" placeholder="Enter an email address...">
                <span class="invalid">{{ errors.first('emailForm.Email Address') }}</span>
                <button class="btn btn-sec" @click="sendEmail">{{ $t('sendEmail') }}</button>
              </div>

              <p class="loading" v-if="emailSending"><i class="fal fa-spinner-third fa-spin"></i> {{ $t('emailSending') }}</p>

              <div class="email-sent" v-if="emailSent">
                <p>{{ $t('bookingConfirmationSentTo', {emailAddress: sendEmailAddress}) }}</p>
                <button class="btn btn-sec" @click="() => {emailSent = false; sendEmailAddress = ''}">{{ $t('sendAnother') }}</button>
              </div>

              <div v-if="emailSendFailed">
                <p>{{ $t('emailSendFailureParagraph') }}</p>
                <button class="btn btn-sec" @click="() => {emailSent = false; sendEmailAddress = ''}">{{ $t('tryAgain') }}</button>
              </div>
            </div>

            <div class="aside-panel booking-support-form">
              <h4>{{ $t('needHelpWithYourBooking') }}</h4>
              <div v-if="!supportRequestSent && !supportRequestSending && !supportRequestFailed">
                <p>{{ $t('bookingHelpFormParagraph') }}</p>
                
                <div class="form-group">
                  <v-select :options="supportOptions.questions" :reduce="item => item.value" data-vv-scope="supportForm" label="label" v-model="questionsSelection"></v-select>
                </div>

                <div v-if="questionsSelection" class="form-group">
                  <div class="form-group">
                    <label :for="$t('contactEmailAddress')">{{ $t('contactEmailAddress') }}</label>
                    <input :id="$t('contactEmailAddress')" :name="$t('contactEmailAddress')" type="text" v-model="userContactEmail" v-validate="'required'" data-vv-scope="supportForm">
                    <span class="invalid">{{ errors.first('supportForm.' + $t('contactEmailAddress')) }}</span>
                  </div>
                  <div class="form-group">
                    <label :for="$t('contactPhoneNumber')">{{ $t('contactPhoneNumber') }}</label>
                    <input :id="$t('contactPhoneNumber')" type="text" :name="$t('contactPhoneNumber')" v-validate="'required'" v-model="userContactPhone" data-vv-scope="supportForm">
                    <span class="invalid">{{ errors.first('supportForm.' + $t('contactPhoneNumber')) }}</span>
                  </div>
                </div>

                <div v-if="questionsSelection === 'passenger'">
                  <div class="form-group">
                    <label :for="$t('passengerInformationType')">{{ $t('passengerInformationType') }}</label>
                    <v-select :id="$t('passengerInformationType')" :name="$t('passengerInformationType')" v-validate="'required'" key="passenger-info" :options="supportOptions.passenger" data-vv-scope="supportForm" v-model="selectedTravelerInfoToChange" :reduce="item => item.value" label="label"></v-select>
                    <span class="invalid">{{ errors.first('supportForm.' + $t('passengerInformationType')) }}</span>
                  </div>
                  <div class="form-group" v-if="selectedTravelerInfoToChange">
                    <label :for="$t('passengerInformationType')">{{ $t('passenger') }}</label>
                    <v-select :id="$t('passengerInformationType')" :name="$t('passenger')" v-validate="'required'" @input="passengerSelected" :options="travelerOptions" data-vv-scope="supportForm" v-model="selectedTraveler"></v-select>
                    <span class="invalid">{{ errors.first('supportForm.' + $t('passenger')) }}</span>
                  </div>
                  <div class="form-group" v-if="selectedTravelerInfoToChange && selectedTravelerInfoToChange === 'name' && selectedTraveler">
                    <label :for="$t('firstName')">{{ $t('firstName') }}</label>
                    <input :id="$t('firstName')" :name="$t('firstName')" type="text" data-vv-scope="supportForm" v-model="newFirstName" v-validate="'required'">
                    <span class="invalid">{{ errors.first('supportForm.' + $t('firstName')) }}</span>
                  </div>
                  <div class="form-group" v-if="selectedTravelerInfoToChange && selectedTravelerInfoToChange === 'name' && selectedTraveler">
                    <label :for="$t('lastName')">{{ $t('lastName') }}</label>
                    <input :id="$t('lastName')" type="text" :name="$t('lastName')" data-vv-scope="supportForm" v-model="newLastName" v-validate="'required'">
                    <span class="invalid">{{ errors.first('supportForm.' + $t('lastName')) }}</span>
                  </div>
                  <div class="form-group" v-if="selectedTravelerInfoToChange && selectedTravelerInfoToChange === 'dateOfBirth' && selectedTraveler">
                    <label :for="$t('dateOfBirth')">{{ $t('dateOfBirth') }}</label>
                    <date-picker :id="$t('dateOfBirth')" :name="$t('dateOfBirth')" v-validate="'required'" v-model="newDateOfBirth" data-vv-scope="supportForm"
                      :input-props='{
                        class: "date-picker",
                        placeholder: "Date of Birth",
                        readonly: true,
                      }'
                      :popover='{
                        visibility: "focus"
                      }'
                      :max-date="new Date()">
                    </date-picker>
                    <span class="invalid">{{ errors.first('supportForm.' + $t('dateOfBirth')) }}</span>
                  </div>
                </div>

                <div v-if="questionsSelection === 'contact'">
                  <div class="form-group">
                    <label :for="$t('contactInformationType')">{{ $t('contactInformationType') }}</label>
                    <v-select :id="$t('contactInformationType')" name="Contact Information Type" v-validate="'required'" key="contact-info" :options="supportOptions.contact" data-vv-scope="supportForm" :reduce="item => item.value" v-model="selectedContactInfoToChange" label="label"></v-select>
                    <span class="invalid">{{ errors.first('supportForm.Contact Information Type') }}</span>
                  </div>
                  <div class="form-group" v-if="selectedContactInfoToChange === 'email'">
                    <label :for="$t('emailAddress')">{{ $t('emailAddress') }}</label>
                    <input :id="$t('emailAddress')" :name="$t('emailAddress')" type="text" v-model="contactEmail" v-validate="'required'" data-vv-scope="supportForm">
                    <span class="invalid">{{ errors.first('supportForm.' + $t('emailAddress')) }}</span>
                  </div>
                  <div class="form-group" v-if="selectedContactInfoToChange === 'phone'">
                    <label :for="$t('phoneNumber')">{{ $t('phoneNumber') }}</label>
                    <input :id="$t('phoneNumber')" type="text" :name="$t('phoneNumber')" v-validate="'required'" v-model="contactPhone" data-vv-scope="supportForm">
                    <span class="invalid">{{ errors.first('supportForm.' + $t('phoneNumber')) }}</span>
                  </div>
                </div>

                <div v-if="questionsSelection === 'rebook'">
                  <div class="form-group">
                    <label :for="$t('departureDate')">{{ $t('departureDate') }}</label>
                    <date-picker :id="$t('departureDate')" :name="$t('departureDate')" v-validate="'required'"
                      v-model="newDepartureDate" data-vv-scope="supportForm"
                      :input-props='{
                        class: "date-picker",
                        placeholder: $t("departureDate"),
                        readonly: true,
                      }'
                      :popover='{
                        visibility: "focus"
                      }'
                      :min-date="new Date()">
                    </date-picker>
                    <span class="invalid">{{ errors.first('supportForm.' + $t('departureDate')) }}</span>
                  </div>

                  <div class="form-group" v-if="airProduct.customerItem.productItem.current.bounds.length > 1">
                    <label :for="$t('returnDate')">{{ $t('returnDate') }}</label>
                    <date-picker :id="$t('returnDate')" data-vv-scope="supportForm"
                      v-model="newReturnDate" v-validate="'required'" :name="$t('returnDate')"
                      :input-props='{
                        class: "date-picker",
                        placeholder: $t("returnDate"),
                        readonly: true,
                      }'
                      :popover='{
                        visibility: "focus"
                      }'
                      :min-date="newDepartureDate">
                    </date-picker>
                    <span class="invalid">{{ errors.first('supportForm.' + $t('returnDate')) }}</span>
                  </div>
                  <div class="form-group">
                    <label :for="$t('routePreference')">{{ $t('routePreference') }}</label>
                    <v-select :id="$t('routePreference')" v-validate="'required'" :name="$t('routePreference')" :options="supportOptions.rebook" data-vv-scope="supportForm" v-model="routePreference" :reduce="item => item.value" label="label"></v-select>
                    <span class="invalid">{{ errors.first('supportForm.' + $t('routePreference')) }}</span>
                  </div>
                </div>

                <div class="form-group" v-if="questionsSelection === 'cancel'">
                  <label :for="$t('cancellationType')">{{ $t('cancellationType') }}</label>
                  <v-select :id="$t('cancellationType')" :name="$t('cancellationType')" data-vv-scope="supportForm" key="cancellation" :options="supportOptions.cancel" v-model="cancellationType" v-validate="'required'" :reduce="item => item.value" label="label"></v-select>
                  <span class="invalid">{{ errors.first('supportForm.' + $t('cancellationType')) }}</span>
                </div>

                <div class="form-group" v-if="questionsSelection === 'other'">
                  <label :for="$t('details')">{{ $t('details') }}</label>
                  <textarea :id="$t('details')" :name="$t('details')" data-vv-scope="supportForm" v-model="otherDetails" v-validate="'required'"></textarea>
                  <span class="invalid">{{ errors.first('supportForm.' + $t('details')) }}</span>
                </div>

                <div class="form-group" v-if="questionsSelection">
                  <button class="btn btn-sec" @click="submitSupportRequest">{{ $t('submit') }}</button>
                </div>
              </div>

              <p class="loading" v-if="supportRequestSending"><i class="fal fa-spinner-third fa-spin"></i> {{ $t('submittingRequest') }}</p>
              
              <div class="email-sent" v-if="supportRequestSent">
                <p>{{ $t('supportRequestSubmittedParagraph') }}</p>
                <button class="btn btn-sec" @click="resetSupportForm">{{ $t('submitAnother') }}</button>
              </div>

              <div v-if="supportRequestFailed">
                <p>{{ $t('supportRequestFailedParagraph') }}</p>
                <button class="btn btn-sec" @click="resetSupportForm">{{ $t('tryAgain') }}</button>
              </div>
            </div>

            <div class="aside-panel booking-support-phone">
              <h4>{{ $t('supportByPhone') }}</h4>
              <p>{{ $t('supportByPhoneFlightsParagraph') }}</p>
              <h5>+1 (646) 558-7089 - ext 8</h5>
            </div>
          </aside>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { auth } from "@/firebaseConfig";
import { mapGetters } from 'vuex'
import ancillaryData from '../../assets/data/ancillaryInfo.json'
import FlightsApi from "../../services/flightsApi";
import DatePicker from 'v-calendar/lib/components/date-picker.umd';

Vue.component('date-picker', DatePicker)

export default {
  name: 'EditBooking',
  data () {
    let self = this;
    return {
      order: null,
      metadata: null,
      ordersCollection: {},
      documents: 0,
      documentNumber: 0,
      documentDate: '',
      event: {},
      authenticated: !!auth.currentUser,
      error: null,
      title: 'Edit Booking',
      orderReference: this.$route.params.orderReference,
      isConfirmation: this.$route.query.isConfirmation,
      email: this.$route.params.email,
      sendEmailAddress: '',
      emailSent: false,
      emailSending: false,
      emailSendFailed: false,
      supportRequestSent: false,
      supportRequestSending: false,
      supportRequestFailed: false,
      api: new FlightsApi(),
      questionsSelection: '',
      newDepartureDate: '',
      newReturnDate: '',
      selectedTraveler: '',
      newFirstName: '',
      newLastName: '',
      newDateOfBirth: '',
      selectedTravelerInfoToChange: '',
      selectedContactInfoToChange: '',
      contactEmail: '',
      contactPhone: '',
      userContactEmail: '',
      userContactPhone: '',
      routePreference: '',
      cancellationType: '',
      otherDetails: '',
      supportOptions: {
        questions: [
          {label: '- ' + self.$t('selectASupportType') + ' -', value: ''},
          {label: self.$t('changePassengerInformation'), value: 'passenger'},
          {label: self.$t('changeContactInformation'), value: 'contact'},
          {label: self.$t('changeFlightRouteDates'), value: 'rebook'},
          {label: self.$t('cancelBooking'), value: 'cancel'},
          {label: self.$t('other'), value: 'other'}
        ],
        passenger: [
          {label: self.$t('firstOrLastName'), value: 'name'},
          {label: self.$t('dateOfBirth'), value: 'dateOfBirth'},
        ],
        contact: [
          {label: self.$t('changeEmailAddress'), value: 'email'},
          {label: self.$t('changePhoneNumber'), value: 'phone'}
        ],
        rebook: [
          {label: self.$t('sameRoute'), value: 'same'},
          {label: self.$t('alternateRoute'), value: 'alternate'}
        ],
        cancel: [
          {label: self.$t('fullyCancelOrder'), value: 'full'},
          {label: self.$t('partiallyCancelOrder'), value: 'partial'}
        ]
      }
    }
  },

  metaInfo: {
    title: 'View Booking',
  },

  methods: {
    isTotalTravelEvent(eventName) {
      return ['initial_booking', 'send_support_request', 'resend_order_confirmation'].includes(eventName);
    },

    resetSupportForm () {
      this.supportRequestSent = false;
      this.supportRequestFailed = false;
      this.supportRequestSending = false;
      this.questionsSelection = '';
      this.newDepartureDate = '';
      this.newReturnDate = '';
      this.selectedTraveler = '';
      this.newFirstName = '';
      this.newLastName = '';
      this.newDateOfBirth = '';
      this.selectedTravelerInfoToChange = '';
      this.selectedContactInfoToChange = '';
      this.contactEmail = '';
      this.contactPhone = '';
      this.routePreference = '';
      this.cancellationType = '';
      this.otherDetails = '';
    },

    async updateOrderServiceFee() {
      let self = this;
      try {
        await self.api.updateOrderServiceFee(self.orderReference, parseFloat(self.metadata.serviceFee), self.metadata.paid);
        self.$notify({
            group: 'saved',
            title: 'Changes Saved',
            type: 'success'
        })
      } catch {
        self.$notify({
            group: 'saved',
            title: 'Error Saving',
            type: 'error'
        })
      }
    },

    passengerSelected () {
      this.newDateOfBirth = this.$moment(this.order.travelers.find(item => item.travelerReference === this.selectedTraveler.value).dateOfBirth).toDate();
    },
    submitSupportRequest () {
      let self = this;

      self.$validator.validateAll('supportForm').then(async (result) => {
        if (result) {
          let subject = '';
          let body = '';
          body += `Order: ${self.orderReference}<br/>`;
          body += `Contact Email: ${self.userContactEmail}<br/>`;
          body += `Contact Phone: +${self.userContactPhone}<br/><br/><br/>`;
          switch (self.questionsSelection) {
            case 'passenger': {
              subject += `Passenger Info Change - ${window.location.host} (TotalTravel) - ${self.orderReference}`;
              let isNameChange = self.selectedTravelerInfoToChange === 'name';
              body +=  isNameChange ? 'Name Change Requested' : 'Date of Birth Change Requested';
              let traveler = self.order.travelers.find(item => item.travelerReference === self.selectedTraveler.value);
              body += ` for Traveler: ${traveler.firstName} ${traveler.lastName} (${traveler.travelerReference}) <br/><br/>`;
              if (isNameChange) {
                body += `Original First Name: "${traveler.firstName}"<br/>`;
                body += `New First Name: "${self.newFirstName}"<br/><br/>`;
                body += `Original Last Name: "${traveler.lastName}"<br/>`;
                body += `New Last Name: "${self.newLastName}"<br/>`;
              }
              else {
                body += `Original Date of Birth: ${traveler.dateOfBirth}<br/>`;
                body += `New Date of Birth: ${self.$moment(self.newDateOfBirth).format('YYYY-MM-DD')}`;
              }
              break;
            }
            case 'contact':{
              subject += `Contact Info Change - ${window.location.host} (TotalTravel) - ${self.orderReference}`;
              let isEmail = this.selectedContactInfoToChange === 'email';
              body += isEmail ? 'Contact Email Change Requested' : 'Contact Phone Change Requested';
              body += '<br/><br/>';
              body += isEmail
                      ? `Original Email: ${self.order.travelers[0].email}`
                      : `Original Phone: +${self.order.travelers[0].phoneNumber.countryCallingCode} ${self.order.travelers[0].phoneNumber.numberWithoutCountryCode}`;
              body += '<br/>';
              body += isEmail
                      ? `New Email: ${self.contactEmail}`
                      : `New Phone: ${self.contactPhone}`;
              break;
            }
            case 'rebook': {
              subject += `Rebooking - ${window.location.host} (TotalTravel) - ${self.orderReference}`
              body += `Rebooking Requested<br/><br/>`;
              body += `Original Departure Date: ${self.airProduct.customerItem.productItem.current.bounds[0].segments[0].from.date}<br/>`;
              body += `New Departure Date: ${self.$moment(self.newDepartureDate).format('YYYY-MM-DD')}<br/><br/>`;
              if (self.airProduct.customerItem.productItem.current.bounds.length > 1) {
                body += `Original Return Date: ${self.airProduct.customerItem.productItem.current.bounds[1].segments[0].from.date}<br/>`;
                body += `New Return Date: ${self.$moment(self.newReturnDate).format('YYYY-MM-DD')}<br/><br/>`;
              }
              body += `Route Preference: ${self.supportOptions.rebook.find(item => item.value === self.routePreference).label}`;

              break;
            }
            case 'cancel': {
              subject += `Cancellation - ${window.location.host} (TotalTravel) - ${self.orderReference}`;
              body += self.cancellationType === 'full' ? `Full Cancellation Requested` : `Partial Cancellation Requested`;
              break;
            }
            case 'other': {
              subject += `General Inquiry - ${window.location.host} (TotalTravel) - ${self.orderReference}`;
              body += `General Request/Inquiry<br/><br/>`;
              body += `Details: ${self.otherDetails}`;
              break;
            }
          }
          // console.log(`Subject: ${subject}`);
          // console.log(`Body: ${body}`);


          self.supportRequestSending = true;
          self.api.sendSupportRequest(self.orderReference, self.userContactEmail, subject, body)
            .then(() => {
              self.supportRequestSent = true;
            })
            .catch(() => {
              self.supportRequestFailed = true;
            })
            .finally(() => {
              self.supportRequestSending = false;
            });
        }
      });

    },

    sendEmail () {
      let self = this;
      self.$validator.validateAll('emailForm').then(async (result) => {
        if (result) {
          self.emailSending = true;
          await self.api.shareFlightOrderDetailsByEmail(self.orderReference, self.sendEmailAddress);
          self.emailSent = true;
          self.emailSending = false;
        }
        else {
          self.emailSending = false;
          self.emailSendFailed = true;
        }
      });
    },
    toAncillaryName (code) {
      let result = ancillaryData[code];
      if (result) {
        return result.title;
      }
      else return code;
    },

    calculateTotalTaxes (orderItems) {
      let result = 0;
      for(let i = 0; i < orderItems.length; i++) {
        let orderItem = orderItems[i];
        if (orderItem.customerItem.productItem.current) {
          for (let j = 0; j < orderItem.customerItem.productItem.current.travelerPrices.length; j++) {
            result += orderItem.customerItem.productItem.current.travelerPrices[j].price.tax;
          }
        }
        else if (orderItem.customerItem.travelerWithPrice) {
          for (let j = 0; j < orderItem.customerItem.productItem.extraProduct.travelerWithPrice.length; j++) {
            if (orderItem.customerItem.productItem.travelerWithPrice[j].price.tax) {
              result += orderItem.customerItem.productItem.travelerWithPrice[j].price.tax;
            }
          }
        }
      }
      return result;
    },

    findAirlineReservationReferences (airProduct, boundIndex, segmentIndex) {
      let result = '';
      let airlineReservationReferences = airProduct.customerItem.productItem.current.airlineReservationReferences;
      // aparently sometimes airlineReservationReferences can be null.  This could be because
      // order has been created on etg side but not the airlines themselves.
      if (airlineReservationReferences) {
        for (let i = 0; i < airlineReservationReferences.length; i++) {
          let reference = airlineReservationReferences[i];
          for (let j = 0; j < reference.segmentIdentifiers.length; j++) {
            let segment = reference.segmentIdentifiers[j];
            if (segment.segmentIndex === segmentIndex && segment.boundIndex === boundIndex) {
              result = reference.reservationReference;
            }
          }
        }
      }

      return result;
    },

    findExtraProductsForTravelerReference (orderItems, travelerReference) {
      let result = [];
      for (let i = 0; i < orderItems.length; i++) {
        let productItem = orderItems[i].customerItem.productItem;
        if (productItem.productType.startsWith('EXTRA.') && productItem.extraProduct.type === 'TRAVELER') {
          for(let j = 0; j < productItem.extraProduct.travelerWithPrice.length; j++) {
            let travelerWithPrice = productItem.extraProduct.travelerWithPrice[j];
            if (travelerWithPrice.travelerReference === travelerReference) {
              result.push(this.toAncillaryName(productItem.productType));
              break;
            }
          }
        }
      }
      return result;
    },
  },

  async created () {
    let self = this;
    let response = await this.api.getOrder(this.orderReference, this.email)
            .catch((err) => {
              self.error = err;
            });
    let responseData = response.data;
    if (response && responseData) {
      self.order = responseData.order;
      self.metadata = responseData.metadata;
      self.event = responseData.event;
      self.documentNumber = responseData.documentNumber;
      self.documents = responseData.documents;
      self.documentDate = new Date(parseInt(responseData.key));
      self.ordersCollection[`${self.orderReference}_${responseData.documentNumber}_${responseData.documents}`] = {
        order: responseData.order,
        event: responseData.event,
        date: new Date(parseInt(responseData.key))
      };
      self.newDepartureDate = self.$moment(self.airProduct.customerItem.productItem.current.bounds[0].segments[0].from.date, 'YYYY-MM-DD').toDate();
      self.newReturnDate = self.airProduct.customerItem.productItem.current.bounds.length > 1
              ? self.$moment(self.airProduct.customerItem.productItem.current.bounds[1].segments[0].from.date, 'YYYY-MM-DD').toDate()
              : '';
      self.userContactEmail = self.contactEmail = self.order.travelers[0].email;
      self.userContactPhone = self.contactPhone = `+${self.order.travelers[0].phoneNumber.countryCallingCode} ${self.order.travelers[0].phoneNumber.numberWithoutCountryCode}`;

    }
  },

  computed: {
    ...mapGetters ([
      'getSite'
    ]),
    site() {
      console.log(this.getSite);
      return this.getSite;
    },

    travelerOptions() {
      return this.order.travelers.map((item) => {
        return {label: `${item.firstName} ${item.lastName}`, value: item.travelerReference};
      })
    },

    airProduct() {
      let orderItems = this.order.primaryOrder.orderItems;
      let airProduct = orderItems.find(item => {
        if (item && item.customerItem && item.customerItem.productItem && item.customerItem.productItem.productType === 'MAIN.AIR') {
          return item;
        }
      });
      return airProduct;
    }
  }
}
</script>
